import { BookOpen, Database, FileText, Home, Library, Link2, Share2 } from 'lucide-react';

import type { Guide } from '../types';

/**
 * Wiki guides — the standalone company knowledge base. Open to all staff
 * (guests are excluded wholesale by the registry).
 */
export const WIKI_GUIDES: Guide[] = [
  {
    slug: 'wiki-overview',
    title: 'What the Wiki is',
    module: 'wiki',
    summary: 'Your company knowledge base — collections, pages and quick access.',
    icon: BookOpen,
    body: `The **Wiki** is the company-wide knowledge base — handbooks, processes,
playbooks and reference docs that everyone shares. It's independent of projects:
project-specific docs live in a project's **Pages** tab; cross-cutting knowledge
lives here.

\`\`\`
 WIKI
 ├─ Home            recents + pinned pages
 ├─ Shared with me  pages someone shared directly
 ├─ Private         your personal pages
 ├─ Archived        soft-deleted, restorable
 └─ Collections     grouped pages (public or private)
     └─ Pages → sub-pages (a tree)
\`\`\`

### The left rail
- **Home** — jump back to recently viewed and pinned pages.
- **Shared with me** — pages a teammate shared with you specifically.
- **Private** — loose pages only you can see (until you share/publish them).
- **Archived** — removed pages you can restore or permanently delete.
- **Collections** — the main library, grouped by topic.

### Who can use it
Every staff member can read what they're entitled to and create personal pages.
Creating **collections** is for admins (or a member granted the permission).`,
  },
  {
    slug: 'wiki-collections',
    title: 'Collections',
    module: 'wiki',
    summary: 'Group related pages; control who can see and edit them.',
    icon: Library,
    body: `A **Collection** groups related pages under one topic (e.g. "Engineering",
"Company Handbook"), with its own icon and access rules.

### Public vs private
- **Public** — every staff member can read the collection and its pages.
- **Private** — only its **members** can see it. Add members and give each a
  role: **Owner** (manage the collection), **Editor** (write pages) or
  **Viewer** (read).

### How to use it
- Click **＋** next to *Collections* to create one (admins, or members granted the
  create-collection permission).
- Open a collection to add pages, manage members (Owners), or **archive** it.
- The owner who creates a collection is added as its first Owner automatically.

### Why it's here
Collections turn a flat pile of pages into a navigable library — and let private
topics (HR, leadership) stay limited to the right people while everything else is
open to the team.`,
  },
  {
    slug: 'wiki-pages',
    title: 'Pages & the editor',
    module: 'wiki',
    summary: 'Write rich pages with a block editor; publish, lock and version them.',
    icon: FileText,
    body: `**Pages** are the documents themselves. They nest into a tree (pages and
sub-pages) inside a collection — or stand alone as personal pages.

### Writing
- The **block editor** drives everything — type \`/\` for commands: headings,
  lists, checklists, tables, callouts, code blocks, images, video and @mentions.
- Give a page an **icon** and title (the title edits inline).

### Draft → publish, lock, history
- A page is a **Draft** until you **Publish** it. Publishing makes it visible to
  its audience (the collection's members, or everyone for a public collection).
- **Lock** a page to freeze edits; **History** lets you view and restore earlier
  versions (every save snapshots the previous one).
- Re-order or re-parent pages by drag-and-drop, or **Move to…** from the ⋯ menu.
- **Archive** a page to remove it (restore or permanently delete it from
  **Archived**).

### Why it's here
One consistent editor and lifecycle for all company knowledge — so docs are easy
to write, safe to change, and never silently lost.`,
  },
  {
    slug: 'wiki-page-embeds',
    title: 'Linking & embedding pages',
    module: 'wiki',
    summary: 'Reference other pages with /page, embed their content, or list sub-pages.',
    icon: Link2,
    body: `Turn a page into a hub by referencing other pages inside it. Type \`/\` in
the editor and pick one of three blocks (these work the same in the standalone
Wiki and in a project's **Pages**):

### \`/page\` — link to a page
Inserts a clean, clickable link (with the page's icon) to another page. Use it
for "see also" references and indexes. Clicking it opens that page.

### \`/page content\` — embed a page's content
Pulls another page's **body inline**, read-only, right where the block sits —
like an "include". Great for reusing a shared section (e.g. a standard
disclaimer or definitions) in several pages without copy-pasting: edit the
source page once and every embed reflects it.

### \`/child pages\` — list sub-pages
Drops in a tidy, indented list of links to the current (or chosen) page's
**sub-pages** — handy as a table of contents at the top of a parent page. The
list follows the page tree and updates as you add or move sub-pages.

### Picking & switching
- An empty block shows a **search box** — type to find the page, click to embed.
- Hover an embed (when editing) to switch it between **Link / Content / Child
  pages**, or **Remove** it.

### Access is always respected
Embeds and child lists only ever show pages **you could already open** — the
server filters them by the same permission rules as direct access, so an embed
never leaks a private or unpublished page. Embedded content is **live** (a
reference, not a frozen copy) and read-only; edit it on the source page.`,
  },
  {
    slug: 'wiki-sharing',
    title: 'Sharing & visibility',
    module: 'wiki',
    summary: 'Who can see a page — collection rules, page visibility, and direct shares.',
    icon: Share2,
    body: `Wiki access layers three controls — all enforced on the server, never just
hidden in the UI.

### 1. Collection
Public collection → all staff can read it. Private collection → its members only.

### 2. Page visibility
Each page is **public** or **private** on its own. A private page in a public
collection stays restricted; a public page is visible to the collection's
audience.

### 3. Direct share
**Share** a single page with specific teammates (view or edit) — they'll find it
under **Shared with me**, even if they're not in the collection.

You can always see a page if you **authored it**, it was **shared with you**, or
it's **public in a context you can access**. Admins (Founder / Super Admin) can
see everything. **There are no public/anonymous links** — sharing is always with
named teammates.

### Why it's here
Knowledge should be open by default but private when it needs to be — without
ever leaking through a guessable link.`,
  },
  {
    slug: 'wiki-home',
    title: 'Home: recents & pinned',
    module: 'wiki',
    summary: 'Get back to the pages you use most, fast.',
    icon: Home,
    body: `**Home** is your launchpad into the Wiki.

### Recently viewed
Every page you open is tracked, so **Recents** lists what you were just in —
no hunting through collections to pick up where you left off.

### Pinned
**Pin** a page (from its ⋯ menu or the editor) to keep it on Home. Pins are
personal — your shortcuts to the handbook section, the runbook, the policy you
check often. Unpin from the card.

### Why it's here
A few pages get used constantly. Home keeps them one click away instead of
several.`,
  },
  {
    slug: 'wiki-sync-kb',
    title: 'Sync to the Knowledge Base',
    module: 'wiki',
    summary: 'Make wiki pages searchable by your AI agents and the assistant.',
    icon: Database,
    body: `Wiki content can feed your **Knowledge Base**, so AI agents and the assistant
retrieve from it (RAG).

### Sync a single page
From a page's **⋯** menu, choose **Sync to Knowledge Base**. Once done, the header
shows a green **Synced · <date>** badge and the menu switches to **Re-sync**.

### Sync a whole collection
Open the collection and click **Sync to Knowledge Base** in its header — every
public page in that collection is indexed into the collection's own KB.

### What's indexed (and what isn't)
- Only **public, non-archived** pages are indexed.
- A page that's **private, archived, or deleted** is never added — and is removed
  if it was synced before.
- Edits re-embed automatically; you rarely need to re-sync by hand.

Private collections get their own KB that only their members (and Founders) can
retrieve from. For the full picture, see the **Knowledge Base** guides —
*Adding knowledge*, *Keeping knowledge fresh*, and *Privacy & access*.`,
  },
];
